import { Toolbar } from '@/ui/Toolbar/Toolbar';
import {
  ActionIcon,
  Affix,
  Badge,
  Burger,
  Collapse,
  Indicator,
  Menu,
  Stack,
  Text,
  Transition,
  rem
} from '@mantine/core';
import { Role } from '../Common/Role';
import { Notifications } from './Notifications';
import { SearchBar } from '@/ui/Toolbar/SearchBar';
import { BlockchainIcon } from './BlockchainIcon';
import { Suspense, useMemo } from 'react';
import { t } from 'i18next';
import { useProfile } from '@/hooks/useProfile';
import { useSmallerView } from '@/hooks/useSmallerView';
import AppDrawer from '../Drawer/AppDrawer';
import { IconArrowUp, IconLogout, IconSettings, IconSignRight } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '@/hooks/useLogout';
import { useAtom } from 'jotai';
import { showDrawerAtom } from '@/ui/Toolbar/atom';
import { IconDotsVertical } from '@tabler/icons-react';
import { useBlockchainCount } from './useBlockchainCount';
import { useWindowScroll } from '@mantine/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { safeLazy } from '../SafeLazy/SafeLazy';
import { useSettings } from '@/hooks/useSettings';

const EndTrial = safeLazy(() => import('./EndTrial'));

const isProd = ['production', 'trackit'].includes(__ENV__);
export const AuthToolbar = () => {
  const user = useProfile();
  const { setSettings } = useSettings();
  const navigate = useNavigate();
  const versionColor = useMemo(() => (!isProd ? 'red' : 'accent'), []);
  const isSmall = useSmallerView('md');
  const logout = useLogout();
  const [opened, setOpened] = useAtom(showDrawerAtom);
  const [scroll, scrollTo] = useWindowScroll();

  const location = useLocation();
  const isTrial = usePermissions('trial');
  const hasBlockchain = usePermissions('blockchain');
  const blockchainCount = useBlockchainCount();

  return (
    <>
      {isTrial && (
        <Suspense>
          <EndTrial />
        </Suspense>
      )}
      <Toolbar
        preLeft={
          <Burger
            opened={opened}
            size="sm"
            color="white"
            mr={import.meta.env.VITE_BRAND === 'seedsbit' ? 'xs' : 0}
            onClick={() => setOpened((open) => !open)}
          />
        }
        left={
          !isSmall && (
            <>{/* <Badge variant="filled" color={versionColor}>{`v${$APP_VERSION}`}</Badge> */}</>
          )
        }
        center={location.pathname !== '/' && <SearchBar />}
        right={
          <>
            <Collapse in={!isSmall}>
              <Stack
                gap={4}
                align="end"
                style={{
                  cursor: 'pointer'
                }}>
                <Text size="sm" c="white" fw="bold" inline>
                  {user.firstName} {user.lastName}
                </Text>
                <Text fw={500} ta="right" size="xs" c="white" opacity={0.6} inline truncate>
                  {user.loggedAsAdmin ? t(`Toolbar.isLoggedAsAdmin`) : user.email}
                </Text>
              </Stack>
            </Collapse>
            <Notifications />
            <Menu zIndex={1005} withArrow arrowSize={12} offset={7}>
              <Menu.Target>
                <Indicator size={8} disabled={blockchainCount === 0} processing color="red.4">
                  <ActionIcon id="options" color="white">
                    <IconDotsVertical size={24} stroke={2} />
                  </ActionIcon>
                </Indicator>
              </Menu.Target>
              <Menu.Dropdown miw={200}>
                <div data-tour="dots">
                  <Menu.Item
                    onClick={() => navigate('/settings')}
                    leftSection={<IconSettings size={18} />}>
                    {t`drawer.industry.menuItems.settings`}
                  </Menu.Item>
                  <Role supplier industry employee>
                    {hasBlockchain && <BlockchainIcon count={blockchainCount} />}
                    <Menu.Item
                      onClick={() => {
                        setSettings({ mainTour: false });
                        navigate('/');
                      }}
                      leftSection={<IconSignRight size={18} />}>{t`drawer.common.tour`}</Menu.Item>
                  </Role>
                  <Menu.Item
                    onClick={logout}
                    leftSection={<IconLogout size={18} />}>{t`drawer.common.action`}</Menu.Item>
                </div>
              </Menu.Dropdown>
            </Menu>
          </>
        }
      />
      <AppDrawer />
      <Affix position={{ bottom: rem(95), right: rem(36) }}>
        <Transition transition="slide-up" mounted={scroll.y > 256}>
          {(transitionStyles) => (
            <ActionIcon
              size="3rem"
              color="accent"
              style={transitionStyles}
              onClick={() => scrollTo({ y: 0 })}
              variant="outline"
              bg="white"
              radius="xl">
              <IconArrowUp size="2rem" />
            </ActionIcon>
          )}
        </Transition>
      </Affix>
    </>
  );
};
